import { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, IeAnexo, ReportService, Route, setPredefinedFiles, ToastServiceStipulator } from '@philips-emr/hpms-stipulator-commons';
import { HpmsCustomDialogAPI, Report } from '@philips-emr/hpms-stipulator-components';
import { HPMSOnCreateMenu, HPMSOnCreateOption, HPMSOnOptionClick } from '@philips-emr/hpms-stipulator-components/lib/events/event';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent } from '@philips-emr/hpms-stipulator-components/lib/types';
import { ActionAPI, BaseFileUploaderAPI, BaseFileUploaderAPIState, BaseFileUploaderAPIStateWithDefaultValues, FetchAPI, FileUploaderAPI, FormAPI, PanelAPI, StampAPI, TextAPI } from '@philips-emr/tws-components';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnChange, OnClick, OnClose, OnDynamicContentRender, OnMount, OnSelect } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, MountEvent, RenderEvent, SelectEvent } from '@philips-emr/tws-core/lib/component/types';
import { FileService, HttpService, MessageService, ModalService } from '@philips-emr/tws-core/lib/services';
import { HttpMethod } from '@philips-emr/tws-core/lib/services/http-service';
import { applicationContext } from '@philips-emr/tws-foundation';
import { isEmptyArrayOrObject, noop } from '@philips-emr/tws-utils';

import { LoteAliases as Aliases, FragmentNames } from '../../codegen';
import { Anexo } from '../../interfaces/anexo';
import BaseController from '../BaseController';

import { BeneficiarioLoteData, getBeneficiarioLoteStampByStatus, getLoteIconColorBySituacao, getLoteIconTypeBySituacao, LoteData, LoteSituacao, onFailedAction, OptionKey } from './lote-utils';

interface Data {
  nrSeqLote: number,
}

@Controller({ fragment: FragmentNames.LOTE })
class LoteController extends BaseController {

  private anexandoArquivo: boolean = false;

  private ieSituacaoLote: LoteSituacao | null = null;

  private regraAnexos: Anexo[];

  @Inject(Aliases.STAMP_SITUACAO_LOTE)
  private stampSituacaoApi: StampAPI;

  @Inject(Aliases.PANEL_CONTROL_CARD)
  private panelControlCardApi: PanelAPI;

  @Inject(Aliases.TEXT_MOTIVO)
  private textMotivoApi: TextAPI;

  @Inject(Aliases.FETCH_BENEFICIARIO_LOTE)
  private fetchBeneficiarioApi: FetchAPI;

  @Inject(Aliases.FORM_ADESAO)
  private formAdesaoApi: FormAPI;

  @Inject(Aliases.PANEL_CONFIRM)
  private panelConfirmApi: PanelAPI;

  @Inject(Aliases.ACTION_PRINT)
  private actionPrintApi: ActionAPI;

  @Inject(Aliases.ACTION_ADD)
  private actionAddApi: ActionAPI;

  @Inject(Aliases.PANEL_FILE_UPLOADER)
  private panelFileUploaderApi: PanelAPI;

  @Inject(Aliases.PANEL_ATTACH)
  private panelAttachments: PanelAPI;

  @Inject(Aliases.FETCH_ANEXOS)
  private fetchAnexos: FetchAPI;

  @Inject(Aliases.PANEL_BUTTON_ANEXOS)
  private panelButtonAnexos: PanelAPI;

  @Inject(Aliases.FILE_UPLOADER_MODAL)
  private fileUploaderModal: BaseFileUploaderAPI<BaseFileUploaderAPIState, BaseFileUploaderAPIStateWithDefaultValues>;

  @Inject(Aliases.HPMS_CUSTOM_DIALOG)
  private customDialog: HpmsCustomDialogAPI;

  @Inject(Aliases.HPMS_CUSTOM_DIALOG_EXCLUIR)
  private customDialogExcluir: HpmsCustomDialogAPI;

  private reportService: ReportService;

  private exibeImprimir: boolean;

  @OnMount(Aliases.PANEL_LOTE)
  onMountPanelLote(): void {
    void this.exibirImprimir().then((resp: boolean) => this.exibeImprimir = resp);
  }

  @OnMount(Aliases.FORM_ADESAO)
  onMountMainPanel(event: MountEvent): void {
    const api = event.target as unknown as FormAPI;
    const { nrSeqLote, nrSeqContrato } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqContrato, nrSeqLote });

    void HttpService.get<LoteData>(Endpoint.GET.LOTE, { nrSeqLote, nrSeqContrato })
      .then(response => {
        const { body } = response;

        if (!body) {
          return;
        }

        this.setSituacaoLote(body, api);
      });
  }

  @OnDynamicContentRender(Aliases.HPMS_CUSTOM_CARD_BENEF_LOTE)
  onDynamicContentRenderCardLote(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, dsStatus } = data as BeneficiarioLoteData;

    const content = (): ReactElement => (getBeneficiarioLoteStampByStatus(ieStatus, dsStatus));

    render(content);
  }

  @OnSelect(Aliases.LOTE_ATTACHMENTS_LIST)
  onSelectAttachment(event: SelectEvent<Anexo>): void {
    const { value } = event;
    const { nrSeqAnexo, filename } = value;
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
    const ieTabela = IeAnexo.INCLUSAO_ANEXOS;

    FileService.download(Endpoint.GET.ANEXO_MODELO_LOTE_DOWNLOAD, filename, { ieTabela, nrSeqAnexo, nrSeqContrato }, HttpMethod.GET)
      .catch(() => ToastServiceStipulator.error(MessageService.translate('stipulator.arquivo-nao-localizado')));
  }

  @HPMSOnCreateMenu(Aliases.HPMS_CUSTOM_CARD_BENEF_LOTE)
  onCreateCardMenu(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, shouldRender } = event;
    const { ieSituacao, ieTipoOperacao } = data as unknown as BeneficiarioLoteData;

    if (ieSituacao === LoteSituacao.REJEITADA_PELA_OPERADORA && ieTipoOperacao !== 'I') {
      shouldRender(false);
    }
  }

  @HPMSOnCreateOption(Aliases.HPMS_CUSTOM_CARD_BENEF_LOTE)
  onCreateOption(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, key, shouldRender } = event;
    const { ieInconsistencia, ieTipoOperacao, ieSituacao } = data as unknown as BeneficiarioLoteData;
    const show = {
      [OptionKey.DETAILS]: ieTipoOperacao === 'I',
      [OptionKey.EXCLUDE]: [LoteSituacao.PENDENTE, LoteSituacao.INTEGRADO_A_PROPOSTA, LoteSituacao.AGUARDANDO_ACAO_DO_USUARIO].includes(ieSituacao),
      [OptionKey.INCONSISTENCIES]: ieInconsistencia === 'S',
      [OptionKey.PRINT]: this.exibeImprimir && (ieSituacao === 'L' || ieSituacao === 'IP'),
    };

    shouldRender(show[key]);
  }

  @HPMSOnOptionClick(Aliases.HPMS_CUSTOM_CARD_BENEF_LOTE)
  onOptionClick(event: HpmsCustomCardEvent<Record<string, Value>>): void {
    const { data, key } = event;
    const { nrSeqInclusao } = data as unknown as BeneficiarioLoteData;

    const action = {
      [OptionKey.DETAILS]: () => this.details(nrSeqInclusao),
      [OptionKey.EXCLUDE]: () => this.exclude(nrSeqInclusao),
      [OptionKey.INCONSISTENCIES]: () => this.inconsistencies(data),
      [OptionKey.PRINT]: () => this.imprimirBeneficiario(nrSeqInclusao),
    };

    if (action[key]) {
      action[key]();
    }
  }

  @OnClick(Aliases.BUTTON_CONFIRM_ADESAO)
  onConfirmAdesaoClick(): void {
    this.customDialog.setVisible(true);
  }

  @OnClick(Aliases.BUTTON_CANCELAR)
  onClickCancelar(): void {
    this.customDialog.setVisible(false);
  }

  @OnClick(Aliases.BUTTON_CONFIRMAR)
  onClickConfirmar(): void {

    const { dtContratacao } = this.formAdesaoApi.getRecord();
    const { nrSeqContrato, nrSeqLote } = this.historyService.getCurrentRouteParams();

    HttpService.post(Endpoint.POST.LOTE_ADESAO, { nrSeqContrato, nrSeqLote }, { body: { dtAdesao: dtContratacao } })
      .then(() => {
        this.customDialog.setVisible(false);
        ToastServiceStipulator.success('stipulator.alt-data');
      })

      .catch(response => onFailedAction(response, this.fetchBeneficiarioApi.forceFetch));

  }

  @OnClick(Aliases.ACTION_ADD)
  onAddBeneficiarioClick(): void {
    const { nrSeqContrato, nrSeqLote } = this.historyService.getCurrentRouteParams();
    applicationContext.redirect(Route.INCLUIR_BENEF_LOTE, { nrSeqContrato, nrSeqLote });
  }

  private setSituacaoLote(data: LoteData, formAdesaoApi: FormAPI): void {
    const { ieSituacao, dsSituacao, dsMotivoRejeicao, dtContratacao } = data;

    this.ieSituacaoLote = ieSituacao;

    this.actionAddApi.setEnabled([LoteSituacao.AGUARDANDO_ACAO_DO_USUARIO, LoteSituacao.COM_INCONSISTENCIAS, LoteSituacao.PENDENTE].includes(ieSituacao));

    if (ieSituacao === LoteSituacao.REJEITADA_PELA_OPERADORA) {
      this.panelControlCardApi.setVisible(true);

      this.textMotivoApi.setValue(dsMotivoRejeicao);

      this.stampSituacaoApi.setColor(getLoteIconColorBySituacao(ieSituacao));
      this.stampSituacaoApi.setIcon(getLoteIconTypeBySituacao(ieSituacao));
      this.stampSituacaoApi.setLabel(dsSituacao);
    }

    const allowsEditing = this.allowsEditingAnexosDataAdesao();

    this.panelConfirmApi.setVisible(allowsEditing);
    formAdesaoApi.setEditing(allowsEditing);
    formAdesaoApi.setRecord({ dtContratacao });
  }

  private allowsEditingAnexosDataAdesao(): boolean {
    if (!this.ieSituacaoLote) {
      return false;
    }

    return [LoteSituacao.AGUARDANDO_ACAO_DO_USUARIO, LoteSituacao.COM_INCONSISTENCIAS, LoteSituacao.PENDENTE].includes(this.ieSituacaoLote);
  }

  @OnMount(Aliases.PANEL_ANEXOS)
  onMountFileUploader(): void {
    const allowsEditing = this.allowsEditingAnexosDataAdesao();
    this.panelAttachments.setVisible(allowsEditing);
  }

  @OnMount(Aliases.PANEL_ATTACH)
  onMountPanelAttach(): void {
    const allowsEditing = this.allowsEditingAnexosDataAdesao();

    if (allowsEditing) {
      void setPredefinedFiles(15, null, ['A', 'LI'])
        .then(anexos => {
          this.panelButtonAnexos.setVisible(!isEmptyArrayOrObject(anexos));
          this.regraAnexos = anexos as unknown as Anexo[];
        });
    }
  }

  @OnMount(Aliases.FILE_UPLOADER_MODAL)
  onMountFileUploaderModal(): void {
    void setPredefinedFiles(15, this.fileUploaderModal, ['A', 'LI'])
      .then(anexos => {
        this.panelButtonAnexos.setVisible(!isEmptyArrayOrObject(anexos));
      });
  }

  @OnClick(Aliases.BUTTON_ENVIAR_ARQUIVOS)
  onClickEnviarArquivos(): void {
    this.panelFileUploaderApi.setVisible(true);
  }

  @OnChange(Aliases.FILE_UPLOADER_MODAL)
  onChangeFileUploaderModal(event: ChangeEvent<{ filename: string, filepath: string, size: number }[]>): void {
    const api = event.target as unknown as BaseFileUploaderAPI<BaseFileUploaderAPIState, BaseFileUploaderAPIStateWithDefaultValues>;
    api.setValue(event.value);
  }

  @OnClick(Aliases.BUTTON_SIM)
  onConfirmarExcluir(): void {
    const { nrSeqContrato, nrSeqLote } = this.historyService.getCurrentRouteParams();
    const { nrSeqSolicitacaoExclusao } = this.context.getBulkData();
    const nrSeqSolicitacao = nrSeqSolicitacaoExclusao;
    if (nrSeqSolicitacao) {
      HttpService.delete(Endpoint.DELETE.BENEFICIARIO_SOLICITACAO, { nrSeqContrato, nrSeqSolicitacao, nrSeqLote })
        .then(() => this.fetchBeneficiarioApi.forceFetch())
        .catch(response => onFailedAction(response, this.fetchBeneficiarioApi.forceFetch));
    }
  }

  @OnClick(Aliases.BUTTON_NAO)
  onCancelarExcluir(): void {
    this.customDialogExcluir.setVisible(false);
  }

  @OnClick(Aliases.ACTION_PRINT)
  onClcikPrint(): void {
    const { nrSeqLote } = this.historyService.getCurrentRouteParams() as unknown as Data;
    this.imprimir(nrSeqLote);
  }

  @OnClose(Aliases.FILE_UPLOADER_MODAL)
  onCloseFileUpload(event: CloseEvent): void {
    this.panelFileUploaderApi.setVisible(false);
    const api = event.target as unknown as FileUploaderAPI;

    // Casting to record 'cause httpservice body doesn't accept array.
    const files = api.getValue() as unknown as Array<Anexo>;

    if (!files || isEmptyArrayOrObject(files) || this.anexandoArquivo) {
      return;
    }
    
    const handledFiles = this.handleAnexos(files) as unknown as Record<string, Value>;

    this.anexandoArquivo = true;

    const { nrSeqLote, nrSeqContrato } = this.historyService.getCurrentRouteParams();

    void HttpService.post(Endpoint.POST.SAVE_ANEXOS_LOTE, { nrSeqLote, nrSeqContrato }, { body: handledFiles })
      .then(() => {
        this.anexandoArquivo = false;
        this.fetchAnexos.forceFetch();
      }).catch(response => {
        onFailedAction(response, noop);
        this.anexandoArquivo = false;
      });
  }

  private exclude = (nrSeqSolicitacaoExclusao: number): void => {
    this.context.set({ nrSeqSolicitacaoExclusao });
    this.customDialogExcluir.setVisible(true);

  };

  private details = (nrSeqInclusao: number): void => {
    this.context.set({ nrSeqInclusao });
    this.historyService.push(Route.DETALHES_BENEF_LOTE, { nrSeqInclusao });
  };

  private inconsistencies = (data: Record<string, Value>): void => {
    const { nrSeqContrato, nrSeqLote } = this.historyService.getCurrentRouteParams();

    ModalService.openDefault({
      title: 'stipulator.inconsistencies',
      fragment: 'inconsistencies-beneficiario',
      contextData: { ...data, nrSeqContrato, nrSeqLote, endpoint: Endpoint.GET.INCONSISTENCIAS_LOTE },
      closeable: true,
    });
  };

  private imprimir(nrSeqLote: number): void {
    this.reportService = new ReportService('17', Endpoint.GET.RELATORIOS_POR_GRUPO);
    this.reportService.abreListaRelatorios({ NR_SEQ_LOTE: nrSeqLote });
  }

  private exibirImprimir = async (): Promise<boolean> => {
    const service = new ReportService('17', Endpoint.GET.RELATORIOS_POR_GRUPO);
    const relatorios = await service.getRelatoriosByCdGrupo() as unknown as Report[];
    const exibe = relatorios.length !== 0;
    this.actionPrintApi.setEnabled(exibe);

    return exibe;
  };

  private imprimirBeneficiario(nrSeqInclusao: number): void {
    this.reportService = new ReportService('16', Endpoint.GET.RELATORIOS_POR_GRUPO);
    this.reportService.abreListaRelatorios({ NR_SEQ_INCLUSAO: nrSeqInclusao });
  }

  private handleAnexos(anexos: Anexo[]): Anexo[] {
    const handledAnexos = anexos.map(anexo => {
      const handledAnexo = { ...anexo };
      const predefinedFileKey = anexo.predefinedFileKey;
      const nrSeqTipoDocumento = this.regraAnexos.find(regraAnexo => +regraAnexo.nrSeqRegraAnexo === +predefinedFileKey)?.nrSeqTipoDocumento ?? 0;
      handledAnexo.nrSeqTipoDocumento = String(nrSeqTipoDocumento);
      return handledAnexo;
    });

    return handledAnexos;
  }
}

export default LoteController;
